import React, { useState, useEffect } from 'react';
import { View, Text, ImageBackground, ScrollView, Image, TouchableOpacity, TextInput, Animated, Platform, StyleSheet, Modal } from 'react-native';
import { getFirestore, collection, query, where, getDocs, doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { globalStyles } from '../styles/globalStyles';
import { adminStyles } from '../styles/adminStyles';
import { myReportsStyles } from '../styles/myReportsStyles';
import { colors } from '../styles/theme';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { extraGradeDisplayFields } from '../utils/extraGradeLines';
import { usePopup } from '../contexts/PopupContext';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { useAnimation } from '../hooks/useAnimation';
import { BREAKPOINTS, getResponsiveValue } from '../styles/breakpoints';
import { useNavigation } from '@react-navigation/native';

const getLetterGrade = (grade) => {
  if (!grade) return 'N/A';
  
  // If it's already a letter grade
  if (typeof grade === 'string') {
    const validGrade = grade.toUpperCase();
    if (['A', 'B', 'C', 'D', 'F'].includes(validGrade)) {
      return validGrade;
    }
  }
  
  // If it's a numeric grade
  if (typeof grade === 'number' || !isNaN(Number(grade))) {
    const numericGrade = Number(grade);
    if (numericGrade >= 4.5) return 'A';
    if (numericGrade >= 3.5) return 'B';
    if (numericGrade >= 2.5) return 'C';
    if (numericGrade >= 1.5) return 'D';
    if (numericGrade >= 1) return 'F';
  }
  
  return 'N/A';
};

const calculateAverageGrade = (ratings) => {
  const gradePoints = {
    'A': 5,
    'B': 4,
    'C': 3,
    'D': 2,
    'F': 1
  };

  const validRatings = Object.values(ratings).filter(rating => {
    if (typeof rating === 'string' && ['A', 'B', 'C', 'D', 'F'].includes(rating.toUpperCase())) {
      return true;
    }
    if (typeof rating === 'number' || !isNaN(Number(rating))) {
      const num = Number(rating);
      return num >= 1 && num <= 5;
    }
    return false;
  });

  if (validRatings.length === 0) return null;

  const sum = validRatings.reduce((acc, rating) => {
    if (typeof rating === 'string') {
      return acc + gradePoints[rating.toUpperCase()];
    }
    return acc + Number(rating);
  }, 0);

  return sum / validRatings.length;
};

export function MyReports({ route }) {
  const navigation = useNavigation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [editingReportId, setEditingReportId] = useState(route.params?.editingReportId || null);
  const [editedFormData, setEditedFormData] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isMobile, setIsMobile] = useState(
    Platform.OS === 'web' && typeof window !== 'undefined' && window.innerWidth <= BREAKPOINTS.mobile
  );
  const { showPopup } = usePopup();
  const fadeIn = useAnimation(0, 1, 500);
  const slideUp = useAnimation(50, 0, 500);

  useEffect(() => {
    loadReports();
  }, []);

  useEffect(() => {
    // If editingReportId is passed in route params, start editing that report
    if (route.params?.editingReportId && route.params?.report) {
      handleEditReport(route.params.report);
    }
  }, [route.params?.editingReportId, route.params?.report]);

  const loadReports = async () => {
    try {
      const db = getFirestore();
      const auth = getAuth();
      
      if (!auth.currentUser) {
        console.error('No user logged in');
        setLoading(false);
        return;
      }

      // Get user data
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }

      // Load reports
      const reportsRef = collection(db, 'venue_reports');
      const q = query(reportsRef, where('userEmail', '==', auth.currentUser.email));
      const querySnapshot = await getDocs(q);
      const reportsArray = [];

      for (const docSnapshot of querySnapshot.docs) {
        const reportData = docSnapshot.data();
        let venueData = null;
        
        if (reportData.venueId) {
          const venueRef = doc(db, 'venues', reportData.venueId);
          const venueDoc = await getDoc(venueRef);
          if (venueDoc.exists()) {
            venueData = venueDoc.data();
          }
        }
        reportsArray.push({
          id: docSnapshot.id,
          ...reportData,
          venue: venueData
        });
      }

      // Sort reports by date
      const sortedReports = reportsArray.sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        return b.createdAt.toDate() - a.createdAt.toDate();
      });

      console.log('Loaded reports:', sortedReports);
      setReports(sortedReports);
      setLoading(false);
    } catch (error) {
      console.error('Error loading reports:', error);
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return colors.success;
      case 'rejected':
        return colors.error;
      case 'pending':
        return colors.warning;
      default:
        return colors.textSecondary;
    }
  };

  const handleEditReport = (report) => {
    setEditingReportId(report.id);
    setEditedFormData({
      // Overall Experience
      overallExperience: report.overallExperience || '',
      
      // Ratings - All possible fields (will be filtered by userType in the form)
      // Artist ratings
      musicalEquipmentGrade: report.musicalEquipmentGrade || '',
      stageSafetyGrade: report.stageSafetyGrade || '',
      hospitality: report.hospitality || '',
      backstage: report.backstage || '',
      venuePromotion: report.venuePromotion || '',
      audioStaff: report.audioStaff || '',
      lightingStaff: report.lightingStaff || '',
      pay: report.pay || '',
      
      // Employee ratings
      audioEquipment: report.audioEquipment || '',
      lightingEquipment: report.lightingEquipment || '',
      workplaceCleaniness: report.workplaceCleaniness || '',
      overallSafety: report.overallSafety || '',
      
      // Touring crew ratings
      venueManagement: report.venueManagement || '',
      cityWalkability: report.cityWalkability || '',
      
      // Other ratings
      safetyRating: report.safetyRating || '',
      equipmentRating: report.equipmentRating || '',
      staffRating: report.staffRating || '',
      workingConditionsRating: report.workingConditionsRating || '',

      // Technical Details
      stage: report.stage || '',
      power: report.power || '',
      lightingControl: report.lightingControl || '',
      lightingFixtures: report.lightingFixtures || '',
      video: report.video || '',
      foh: report.foh || '',
      pa: report.pa || '',
      monitors: report.monitors || '',
      backline: report.backline || '',

      // Facilities
      parking: report.parking || '',
      shorePower: report.shorePower || '',
      merch: report.merch || '',
      adaAccessibilityArtist: report.adaAccessibilityArtist || '',

      // Media handling
      media: report.media || {},
      photos: report.photos || [],
      videos: report.videos || [],

      // Additional fields
      dateWorked: report.dateWorked || '',
      notes: report.notes || '',
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (reportId) => {
    try {
      const db = getFirestore();
      const reportRef = doc(db, 'venue_reports', reportId);

      // Get the current report to check if it's tech specs only
      const reportDoc = await getDoc(reportRef);
      const currentReport = reportDoc.data();
      const isTechSpecsOnly = currentReport.isTechSpecsOnly;

      // Convert media object to arrays for consistency with Admin view
      const mediaUrls = Object.values(editedFormData.media || {});
      
      // Create base update data
      const updateData = {
        ...editedFormData,
        // Format media the same way as Admin.js
        photos: mediaUrls.filter(url => url.match(/\.(jpg|jpeg|png|gif)$/i)),
        videos: mediaUrls.filter(url => url.match(/\.(mp4|mov|avi|wmv)$/i)),
        // Keep original media object for backwards compatibility
        media: editedFormData.media,
        updatedAt: new Date(),
      };

      // If it's a tech specs only report, remove experience-related fields
      if (isTechSpecsOnly) {
        delete updateData.overallExperience;
        // Remove all rating fields for tech specs
        delete updateData.safetyRating;
        delete updateData.equipmentRating;
        delete updateData.staffRating;
        delete updateData.workingConditionsRating;
        delete updateData.musicalEquipmentGrade;
        delete updateData.stageSafetyGrade;
        delete updateData.hospitality;
        delete updateData.venuePromotion;
        delete updateData.audioStaff;
        delete updateData.lightingStaff;
        delete updateData.pay;
        delete updateData.audioEquipment;
        delete updateData.lightingEquipment;
        delete updateData.workplaceCleaniness;
        delete updateData.overallSafety;
        delete updateData.venueManagement;
        delete updateData.cityWalkability;
      }

      await updateDoc(reportRef, updateData);
      setEditingReportId(null);
      setShowEditModal(false);
      showPopup('Report updated successfully', 'success');
      loadReports(); // Refresh the reports list
    } catch (error) {
      console.error('Error updating report:', error);
      showPopup('Error updating report', 'error');
    }
  };

  const handleRevokeReport = async (report) => {
    if (window.confirm('Are you sure you want to revoke this report? This action cannot be undone.')) {
      try {
        const db = getFirestore();
        const reportRef = doc(db, 'venue_reports', report.id);

        await deleteDoc(reportRef);
        showPopup('Report successfully revoked', 'success');
        loadReports();
      } catch (error) {
        console.error('Error revoking report:', error);
        showPopup('Error revoking report. Please try again.', 'error');
      }
    }
  };

  const handleMediaUpload = async (reportId, files) => {
    try {
      const storage = getStorage();
      const currentMediaCount = Object.keys(editedFormData.media || {}).length;
      const filesArray = Array.from(files);

      // Check if adding these files would exceed the limit
      if (currentMediaCount + filesArray.length > 4) {
        showPopup('Maximum 4 files allowed', 'error');
        return;
      }

      // Start with existing media
      const updatedMedia = { ...editedFormData.media };
      const newPhotos = [...(editedFormData.photos || [])];
      const newVideos = [...(editedFormData.videos || [])];

      for (const file of filesArray) {
        const storageRef = ref(storage, `reports/${reportId}/${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        updatedMedia[file.name] = url;
        
        // Add to appropriate array based on file type
        if (url.match(/\.(jpg|jpeg|png|gif)$/i)) {
          newPhotos.push(url);
        } else if (url.match(/\.(mp4|mov|avi|wmv)$/i)) {
          newVideos.push(url);
        }
      }

      setEditedFormData(prev => ({
        ...prev,
        media: updatedMedia,
        photos: newPhotos,
        videos: newVideos
      }));

      showPopup('Media uploaded successfully', 'success');
    } catch (error) {
      console.error('Error uploading media:', error);
      showPopup('Error uploading media', 'error');
    }
  };

  const handleDeleteMedia = async (reportId, fileName, url) => {
    try {
      const storage = getStorage();
      const fileRef = ref(storage, `reports/${reportId}/${fileName}`);
      await deleteObject(fileRef);

      const updatedMedia = { ...editedFormData.media };
      delete updatedMedia[fileName];

      // Remove from photos or videos array
      const newPhotos = editedFormData.photos?.filter(u => u !== url) || [];
      const newVideos = editedFormData.videos?.filter(u => u !== url) || [];

      setEditedFormData(prev => ({
        ...prev,
        media: updatedMedia,
        photos: newPhotos,
        videos: newVideos
      }));

      showPopup('Media deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting media:', error);
      showPopup('Error deleting media', 'error');
    }
  };

  const renderReport = (report) => {
    const techSpecsFields = [
      { label: 'Stage', field: 'stage' },
      { label: 'Power', field: 'power' },
      { label: 'Lighting Control', field: 'lightingControl' },
      { label: 'Lighting Fixtures', field: 'lightingFixtures' },
      { label: 'Video', field: 'video' },
      { label: 'FOH', field: 'foh' },
      { label: 'PA System', field: 'pa' },
      { label: 'Monitors', field: 'monitors' },
      { label: 'Backline', field: 'backline' },
      { label: 'Backstage', field: 'backstage' },
      { label: 'Parking', field: 'parking' },
      { label: 'Shore Power', field: 'shorePower' },
      { label: 'Merch', field: 'merch' },
      { label: 'ADA Accessibility for Artist', field: 'adaAccessibilityArtist' }
    ];

    // Check if this is a tech specs submission
    const hasTechSpecs = techSpecsFields.some(field => report[field.field]);

    return (
      <View key={report.id} style={styles.reportCard}>
        <View style={styles.reportHeader}>
          <View style={{ flex: 1 }}>
            <Text style={styles.venueName}>{report.venueName}</Text>
            <Text style={styles.reportDate}>
              Submitted: {report.createdAt?.toDate().toLocaleDateString()}
            </Text>
          </View>
          <View style={styles.headerBadges}>
            {report.isTechSpecsOnly && (
              <View style={[styles.statusBadge, { backgroundColor: '#9b4dff' }]}>
                <Text style={styles.statusBadgeText}>TECH SPECS</Text>
              </View>
            )}
            <View style={[styles.statusBadge, { backgroundColor: getStatusColor(report.status) }]}>
              <Text style={styles.statusBadgeText}>{report.status?.toUpperCase()}</Text>
            </View>
          </View>
        </View>

        <View style={styles.reportContent}>
          {report.status === 'rejected' && report.denyReason && (
            <View style={styles.denyReasonSection}>
              <Text style={styles.denyReasonTitle}>Rejection Reason:</Text>
              <Text style={styles.denyReasonText}>{report.denyReason}</Text>
            </View>
          )}
          {report.isTechSpecsOnly || hasTechSpecs ? (
            // Tech Specs Display
            <View style={styles.techSpecsSection}>
              <Text style={styles.sectionTitle}>Technical Specifications</Text>
              <View style={styles.techSpecsGrid}>
                {techSpecsFields.map(({ label, field }) => (
                  report[field] && (
                    <View key={field} style={styles.techSpecItem}>
                      <Text style={styles.techSpecLabel}>{label}:</Text>
                      <Text style={styles.techSpecValue}>{report[field]}</Text>
                    </View>
                  )
                ))}
              </View>
            </View>
          ) : (
            // Regular Report Display
            <>
              {report.overallExperience && (
                <View style={styles.experienceSection}>
                  <Text style={styles.sectionTitle}>Overall Experience</Text>
                  <Text style={styles.experienceText}>{report.overallExperience}</Text>
                </View>
              )}
              
              {/* Display ratings based on userType */}
              {(() => {
                const userType = report.userType || 'other';
                const ratingFields = [];
                
                // Define rating fields based on userType (matching VenueReportForm.js)
                if (userType === 'artist') {
                  ratingFields.push(
                    { label: 'Musical Equipment', field: 'musicalEquipmentGrade' },
                    { label: 'Stage Safety', field: 'stageSafetyGrade' },
                    { label: 'Hospitality', field: 'hospitality' },
                    { label: 'Backstage', field: 'backstage' },
                    { label: 'Venue Promotion', field: 'venuePromotion' },
                    { label: 'Audio Staff', field: 'audioStaff' },
                    { label: 'Lighting Staff', field: 'lightingStaff' },
                    { label: 'Pay', field: 'pay' }
                  );
                } else if (userType === 'employee') {
                  ratingFields.push(
                    { label: 'Audio Equipment', field: 'audioEquipment' },
                    { label: 'Lighting Equipment', field: 'lightingEquipment' },
                    { label: 'Workplace Cleanliness', field: 'workplaceCleaniness' },
                    { label: 'Overall Safety', field: 'overallSafety' }
                  );
                } else if (userType === 'touring') {
                  ratingFields.push(
                    { label: 'Venue Management', field: 'venueManagement' },
                    { label: 'Audio Staff', field: 'audioStaff' },
                    { label: 'Lighting Staff', field: 'lightingStaff' },
                    { label: 'Audio Equipment', field: 'audioEquipment' },
                    { label: 'Lighting Equipment', field: 'lightingEquipment' },
                    { label: 'Overall Safety', field: 'overallSafety' },
                    { label: 'City Walkability', field: 'cityWalkability' }
                  );
                } else {
                  // Other or default
                  ratingFields.push(
                    { label: 'Safety', field: 'safetyRating' },
                    { label: 'Equipment', field: 'equipmentRating' },
                    { label: 'Staff', field: 'staffRating' },
                    { label: 'Working Conditions', field: 'workingConditionsRating' }
                  );
                }
                
                // Filter to only show ratings that exist
                const existingRatings = ratingFields.filter(({ field }) => report[field]);
                const extraRatings = extraGradeDisplayFields(report);
                
                if (existingRatings.length === 0 && extraRatings.length === 0) return null;
                
                return (
                  <View style={styles.ratingsSection}>
                    <Text style={styles.sectionTitle}>Ratings</Text>
                    <View style={styles.ratingsGrid}>
                      {existingRatings.map(({ label, field }) => (
                        <View key={field} style={styles.ratingItem}>
                          <Text style={styles.ratingLabel}>{label}:</Text>
                          <Text style={styles.ratingValue}>{getLetterGrade(report[field])}</Text>
                        </View>
                      ))}
                      {extraRatings.map((line) => (
                        <View key={line.field} style={styles.ratingItem}>
                          <Text style={styles.ratingLabel}>{line.label}:</Text>
                          <Text style={styles.ratingValue}>{getLetterGrade(line.value)}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                );
              })()}
            </>
          )}

          {report.notes && (
            <View style={styles.notesSection}>
              <Text style={styles.sectionTitle}>Additional Notes</Text>
              <Text style={styles.notesText}>{report.notes}</Text>
            </View>
          )}

          {/* Media Section */}
          {report.photos && report.photos.length > 0 && (
            <View style={styles.mediaSection}>
              <Text style={styles.sectionTitle}>Photos</Text>
              <ScrollView horizontal style={styles.mediaScroll}>
                {report.photos.map((url, index) => (
                  <View key={index} style={styles.mediaItem}>
                    <Image source={{ uri: url }} style={styles.mediaPreview} />
                  </View>
                ))}
              </ScrollView>
            </View>
          )}

          <View style={styles.actionButtons}>
            {report.status === 'pending' && (
              <TouchableOpacity
                style={[styles.actionButton, styles.editButton]}
                onPress={() => handleEditReport(report)}
              >
                <Text style={styles.actionButtonText}>Edit</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[styles.actionButton, styles.deleteButton]}
              onPress={() => handleRevokeReport(report)}
            >
              <Text style={styles.actionButtonText}>Delete</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  const renderEditModal = () => {
    if (!editedFormData) return null;

    const techSpecsFields = [
      { label: 'Stage', field: 'stage' },
      { label: 'Power', field: 'power' },
      { label: 'Lighting Control', field: 'lightingControl' },
      { label: 'Lighting Fixtures', field: 'lightingFixtures' },
      { label: 'Video', field: 'video' },
      { label: 'FOH', field: 'foh' },
      { label: 'PA System', field: 'pa' },
      { label: 'Monitors', field: 'monitors' },
      { label: 'Backline', field: 'backline' },
      { label: 'Backstage', field: 'backstage' },
      { label: 'Parking', field: 'parking' },
      { label: 'Shore Power', field: 'shorePower' },
      { label: 'Merch', field: 'merch' },
      { label: 'ADA Accessibility for Artist', field: 'adaAccessibilityArtist' }
    ];

    const currentReport = reports.find(r => r.id === editingReportId);
    const hasTechSpecs = techSpecsFields.some(field => currentReport?.[field.field]);

    return (
      <Modal
        visible={showEditModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {
          setShowEditModal(false);
          setEditingReportId(null);
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Edit Report</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => {
                  setShowEditModal(false);
                  setEditingReportId(null);
                }}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalContent}>
              {currentReport?.isTechSpecsOnly || hasTechSpecs ? (
                // Tech Specs Edit Form
                <View>
                  <Text style={styles.formSectionTitle}>Technical Specifications</Text>
                  {techSpecsFields.map(({ label, field }) => (
                    <View key={field} style={styles.formGroup}>
                      <Text style={styles.formLabel}>{label}:</Text>
                      <TextInput
                        style={styles.formInput}
                        value={editedFormData[field]}
                        onChangeText={(value) => setEditedFormData(prev => ({ ...prev, [field]: value }))}
                        placeholder={`Enter ${label.toLowerCase()} details...`}
                        placeholderTextColor="rgba(0, 0, 0, 0.5)"
                        multiline
                      />
                    </View>
                  ))}
                </View>
              ) : (
                // Regular Report Edit Form
                <>
                  <View style={styles.formGroup}>
                    <Text style={styles.formLabel}>Overall Experience:</Text>
                    <TextInput
                      style={[styles.formInput, styles.textArea]}
                      multiline
                      numberOfLines={4}
                      value={editedFormData.overallExperience}
                      onChangeText={(value) => setEditedFormData(prev => ({ ...prev, overallExperience: value }))}
                      placeholder="Describe your overall experience..."
                      placeholderTextColor="rgba(0, 0, 0, 0.5)"
                    />
                  </View>

                  {/* Rating Fields - based on userType */}
                  <Text style={styles.formSectionTitle}>Ratings</Text>
                  
                  {(() => {
                    const userType = currentReport?.userType || 'other';
                    const ratingFields = [];
                    
                    // Define rating fields based on userType (matching VenueReportForm.js)
                    if (userType === 'artist') {
                      ratingFields.push(
                        { label: 'Musical Equipment Grade', field: 'musicalEquipmentGrade' },
                        { label: 'Stage Safety Grade', field: 'stageSafetyGrade' },
                        { label: 'Hospitality', field: 'hospitality' },
                        { label: 'Backstage', field: 'backstage' },
                        { label: 'Venue Promotion', field: 'venuePromotion' },
                        { label: 'Audio Staff', field: 'audioStaff' },
                        { label: 'Lighting Staff', field: 'lightingStaff' },
                        { label: 'Pay', field: 'pay' }
                      );
                    } else if (userType === 'employee') {
                      ratingFields.push(
                        { label: 'Audio Equipment', field: 'audioEquipment' },
                        { label: 'Lighting Equipment', field: 'lightingEquipment' },
                        { label: 'Workplace Cleanliness', field: 'workplaceCleaniness' },
                        { label: 'Overall Safety', field: 'overallSafety' }
                      );
                    } else if (userType === 'touring') {
                      ratingFields.push(
                        { label: 'Venue Management', field: 'venueManagement' },
                        { label: 'Audio Staff', field: 'audioStaff' },
                        { label: 'Lighting Staff', field: 'lightingStaff' },
                        { label: 'Audio Equipment', field: 'audioEquipment' },
                        { label: 'Lighting Equipment', field: 'lightingEquipment' },
                        { label: 'Overall Safety', field: 'overallSafety' },
                        { label: 'City Walkability', field: 'cityWalkability' }
                      );
                    } else {
                      // Other or default
                      ratingFields.push(
                        { label: 'Safety Rating', field: 'safetyRating' },
                        { label: 'Equipment Rating', field: 'equipmentRating' },
                        { label: 'Staff Rating', field: 'staffRating' },
                        { label: 'Working Conditions Rating', field: 'workingConditionsRating' }
                      );
                    }
                    
                    return ratingFields.map(({ label, field }) => (
                      <View key={field} style={styles.formGroup}>
                        <Text style={styles.formLabel}>{label}:</Text>
                        <TextInput
                          style={styles.formInput}
                          value={editedFormData[field] || ''}
                          onChangeText={(value) => setEditedFormData(prev => ({ ...prev, [field]: value }))}
                          placeholder="A, B, C, D, or F"
                          placeholderTextColor="rgba(0, 0, 0, 0.5)"
                        />
                      </View>
                    )).concat(extraGradeDisplayFields(currentReport).map((line) => (
                      <View key={line.field} style={styles.formGroup}>
                        <Text style={styles.formLabel}>{line.label}:</Text>
                        <Text style={styles.formInput}>{getLetterGrade(line.value)}</Text>
                      </View>
                    )));
                  })()}
                </>
              )}

              {/* Media Section */}
              <Text style={styles.formSectionTitle}>Media</Text>
              <View style={styles.mediaEditSection}>
                {editedFormData.media && Object.entries(editedFormData.media).map(([fileName, url], index) => (
                  <View key={index} style={styles.mediaEditItem}>
                    <Image source={{ uri: url }} style={styles.mediaEditPreview} />
                    <TouchableOpacity
                      style={styles.deleteMediaButton}
                      onPress={() => handleDeleteMedia(editingReportId, fileName, url)}
                    >
                      <Text style={styles.deleteMediaButtonText}>×</Text>
                    </TouchableOpacity>
                  </View>
                ))}
                
                {(!editedFormData.media || Object.keys(editedFormData.media).length < 4) && (
                  <TouchableOpacity
                    style={styles.addMediaButton}
                    onPress={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*,video/*';
                      input.multiple = true;
                      input.onchange = (e) => handleMediaUpload(editingReportId, e.target.files);
                      input.click();
                    }}
                  >
                    <Text style={styles.addMediaButtonText}>+ Add Media</Text>
                  </TouchableOpacity>
                )}
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Date Worked:</Text>
                <TextInput
                  style={styles.formInput}
                  value={editedFormData.dateWorked}
                  onChangeText={(value) => setEditedFormData(prev => ({ ...prev, dateWorked: value }))}
                  placeholder="Enter date worked..."
                  placeholderTextColor="rgba(0, 0, 0, 0.5)"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Additional Notes:</Text>
                <TextInput
                  style={[styles.formInput, styles.textArea]}
                  multiline
                  numberOfLines={4}
                  value={editedFormData.notes}
                  onChangeText={(value) => setEditedFormData(prev => ({ ...prev, notes: value }))}
                  placeholder="Any additional notes..."
                  placeholderTextColor="rgba(0, 0, 0, 0.5)"
                />
              </View>
            </ScrollView>

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => {
                  setShowEditModal(false);
                  setEditingReportId(null);
                }}
              >
                <Text style={styles.modalButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.saveButton]}
                onPress={() => handleSaveEdit(editingReportId)}
              >
                <Text style={styles.modalButtonText}>Save Changes</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  return (
    <View style={globalStyles.container}>
      <BackgroundVideo />
      <View style={globalStyles.overlay}>
        <ScrollView style={styles.scrollContainer}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.backButtonText}>← Back</Text>
          </TouchableOpacity>

          <Text style={styles.title}>MY REPORTS</Text>
          
          <View style={styles.contentContainer}>
            {loading ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyStateText}>Loading reports...</Text>
              </View>
            ) : reports.length === 0 ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyStateText}>No reports submitted yet</Text>
                <Text style={styles.emptyStateSubtext}>
                  Submit your first venue report to see it here
                </Text>
              </View>
            ) : (
              <>
                <Text style={styles.reportCount}>
                  {reports.length} report{reports.length !== 1 ? 's' : ''} found
                </Text>
                <View style={styles.reportsGrid}>
                  {reports.map((report) => renderReport(report))}
                </View>
              </>
            )}
          </View>
        </ScrollView>
        
        {renderEditModal()}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollContainer: {
    width: '100%',
    maxWidth: 1200,
    alignSelf: 'center',
    padding: 20,
  },
  backButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 10,
    alignItems: 'center',
    marginBottom: 20,
    alignSelf: 'flex-start',
    minWidth: 100,
  },
  backButtonText: {
    color: '#000000',
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
  },
  title: {
    fontFamily: 'Playpen Sans',
    fontSize: getResponsiveValue({
      mobile: 36,
      tablet: 42,
      laptop: 48,
      desktop: 48
    }),
    color: '#000000',
    marginBottom: 30,
    textAlign: 'center',
    textShadow: '0px 2px 4px rgba(0,0,0,0.25)',
  },
  contentContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 10,
    padding: getResponsiveValue({
      mobile: 15,
      tablet: 25,
      laptop: 30,
      desktop: 30
    }),
    width: '100%',
    alignSelf: 'center',
    marginBottom: 40,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  emptyStateText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 18,
    color: '#000000',
    textAlign: 'center',
    marginBottom: 10,
  },
  emptyStateSubtext: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: 'rgba(0, 0, 0, 0.7)',
    textAlign: 'center',
  },
  reportCount: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    color: '#000000',
    marginBottom: 20,
    textAlign: 'center',
    opacity: 0.8,
  },
  reportsGrid: {
    gap: 20,
  },
  reportCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 10,
    padding: 20,
    marginBottom: 15,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  reportHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 15,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
  venueName: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 18,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 5,
  },
  reportDate: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    color: 'rgba(0, 0, 0, 0.6)',
  },
  headerBadges: {
    flexDirection: 'row',
    gap: 8,
  },
  statusBadge: {
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  statusBadgeText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 10,
    fontWeight: 'bold',
    color: '#ffffff',
    textTransform: 'uppercase',
  },
  reportContent: {
    gap: 15,
  },
  sectionTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 10,
  },
  experienceSection: {
    marginBottom: 15,
  },
  experienceText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: '#000000',
    lineHeight: 20,
  },
  ratingsSection: {
    marginBottom: 15,
  },
  ratingsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  ratingItem: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderRadius: 8,
    padding: 10,
    minWidth: '45%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  ratingLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    fontWeight: 'bold',
    color: 'rgba(0, 0, 0, 0.7)',
    flex: 1,
  },
  ratingValue: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000000',
    minWidth: 30,
    textAlign: 'center',
  },
  techSpecsSection: {
    marginBottom: 15,
  },
  techSpecsGrid: {
    gap: 10,
  },
  techSpecItem: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderRadius: 8,
    padding: 12,
  },
  techSpecLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    fontWeight: 'bold',
    color: 'rgba(0, 0, 0, 0.7)',
    marginBottom: 4,
  },
  techSpecValue: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: '#000000',
    lineHeight: 18,
  },
  notesSection: {
    marginBottom: 15,
  },
  notesText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: '#000000',
    lineHeight: 20,
  },
  denyReasonSection: {
    backgroundColor: 'rgba(231, 76, 60, 0.1)',
    borderRadius: 8,
    padding: 12,
    marginBottom: 15,
    borderLeftWidth: 4,
    borderLeftColor: '#e74c3c',
  },
  denyReasonTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: 'bold',
    color: '#e74c3c',
    marginBottom: 6,
  },
  denyReasonText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: '#000000',
    lineHeight: 20,
  },
  mediaSection: {
    marginBottom: 15,
  },
  mediaScroll: {
    flexDirection: 'row',
  },
  mediaItem: {
    marginRight: 10,
  },
  mediaPreview: {
    width: 80,
    height: 80,
    borderRadius: 8,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
  },
  actionButton: {
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 80,
  },
  editButton: {
    backgroundColor: '#2ecc71',
  },
  deleteButton: {
    backgroundColor: '#e74c3c',
  },
  actionButtonText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    fontWeight: '500',
  },
  
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 10,
    width: '100%',
    maxWidth: 600,
    maxHeight: '80%',
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
  modalTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 18,
    fontWeight: 'bold',
    color: '#000000',
  },
  modalCloseButton: {
    padding: 8,
    borderRadius: 15,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  modalCloseText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000000',
  },
  modalContent: {
    padding: 20,
    maxHeight: 400,
  },
  formSectionTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 15,
  },
  formGroup: {
    marginBottom: 15,
  },
  formLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: '#000000',
    marginBottom: 8,
    fontWeight: '500',
  },
  formInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 8,
    padding: 12,
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: '#000000',
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.2)',
    minHeight: 40,
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0, 0, 0, 0.1)',
  },
  modalButton: {
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 100,
  },
  cancelButton: {
    backgroundColor: '#95a5a6',
  },
  saveButton: {
    backgroundColor: '#2ecc71',
  },
  modalButtonText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: '500',
  },
  
  // Media editing styles
  mediaEditSection: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 15,
  },
  mediaEditItem: {
    position: 'relative',
    width: 80,
    height: 80,
  },
  mediaEditPreview: {
    width: 80,
    height: 80,
    borderRadius: 8,
  },
  deleteMediaButton: {
    position: 'absolute',
    top: -5,
    right: -5,
    backgroundColor: '#e74c3c',
    borderRadius: 12,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteMediaButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
    lineHeight: 16,
  },
  addMediaButton: {
    width: 80,
    height: 80,
    backgroundColor: 'rgba(46, 204, 113, 0.1)',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#2ecc71',
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addMediaButtonText: {
    color: '#2ecc71',
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    fontWeight: '500',
    textAlign: 'center',
  },
}); 